import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Icon,
  IconButton,
  Logo,
  Menu,
  MenuItem,
  MenuSection,
  MenuSeparator,
  ThemeModeButton,
} from '@acs/ui';
import { signOutUser } from '../shared/api/auth.js';
import { useAuth } from '../features/auth/index.js';
import { LanguageSelect, useI18n } from '../i18n/index.js';

interface AppHeaderProps {
  /**
   * How much of the brand lock-up to show.
   *
   * `full` (wordmark + mark) is reserved for the account-level pages, where the
   * bar carries nothing else and the product still has to introduce itself.
   * Everywhere inside a business the mark alone stands in: the header's job
   * there is to say which BUSINESS you are in, and a wordmark set at the same
   * size as the business name competes with it for that answer on every page.
   */
  brand?: 'full' | 'mark';
  /** The active business's name; omitted on the account-level pages. */
  switcher?: ReactNode;
  /** Primary navigation; hidden below the compact breakpoint (drawer instead). */
  nav?: ReactNode;
  /** Full-bleed strip under the bar (needs-attention / provisioning). */
  banner?: ReactNode;
  /** Opens the mobile nav drawer. Omit to hide the hamburger entirely. */
  onOpenMenu?: () => void;
}

/**
 * The single top bar every page renders (business shell + account pages), so
 * the brand mark lands in exactly the same place on every route. Pages pass
 * their section-specific controls in; the account block is always ours.
 */
export function AppHeader({ brand = 'mark', switcher, nav, banner, onOpenMenu }: AppHeaderProps) {
  const { t } = useI18n();
  return (
    <AppBar
      start={
        <>
          {/* Below the compact breakpoint the drawer trigger IS the brand mark.
              The bar there had two marks in a row — a hamburger, then the logo
              beside it linking home — which is two symbols for a phone-width
              header that also has to carry the business name and the account
              button. One of them had to go, and the logo is the one that can do
              both jobs: it is already the most recognisable thing in the bar, and
              a tapped logo opening the app's own navigation is an idiom nobody
              has to be taught. The link home lives in the drawer's own heading
              and in the account menu's "My businesses".
              `aria-label` still says "open menu", because that is what it does.
              Framed as a square button around a SMALL mark rather than the bare
              logo: on its own the mark read as decoration, and nobody tapped it
              expecting a menu. The frame says "this is a control" the way the
              round account button opposite it does. */}
          {onOpenMenu && (
            <IconButton
              className="shell-compact-only appbar-menu"
              aria-label={t('nav.openMenu')}
              // `aria-hidden` on the mark: the Logo labels itself "TopCall24"
              // for the pages that use it as a brand, and inside a button that
              // already says what it does, that is a second announcement for
              // one control.
              icon={<Logo markOnly size="sm" aria-hidden />}
              onClick={onOpenMenu}
            />
          )}
          <Link
            to="/businesses"
            aria-label={t('account.home')}
            className={`appbar-brand appbar-brand--${brand}${onOpenMenu ? ' shell-wide-only' : ''}`}
          >
            <Logo markOnly={brand !== 'full'} />
          </Link>
          {/* No rule between the mark and the business name. The one that used
              to sit here was standing in for the switcher's lost border, back
              when a full wordmark ran into the business name at the same size
              and the bar read as one long phrase. With the mark alone there are
              no two labels to separate — just a symbol and a title, which is
              the ordinary shape of a header.
              The trailing rule stays: the nav beside it IS a row of labels.
              It follows the nav below the compact breakpoint, where the nav
              becomes a drawer and it would rule off nothing. */}
          {switcher}
          {switcher && nav && <span className="appbar-divider shell-wide-only" aria-hidden />}
        </>
      }
      center={nav ? <div className="shell-wide-only" style={{ display: 'flex', minWidth: 0 }}>{nav}</div> : undefined}
      end={<UserMenu />}
      banner={banner}
    />
  );
}

/**
 * Identity, businesses, theme, language, sign out (design template §user menu).
 * Exported for the setup wizard's bar, which is not built on `AppHeader`.
 */
export function UserMenu() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const name = user?.displayName ?? user?.email ?? t('account.menuLabel');

  return (
    <Menu
      aria-label={t('account.menuLabel')}
      align="end"
      width={260}
      trigger={
        // A plain account icon rather than the signed-in person's avatar: the
        // button opens account SETTINGS, and the identity it would illustrate
        // is already spelled out in the first row of the menu it opens.
        <button type="button" className="user-trigger" aria-label={t('account.label', { name })}>
          <Icon name="user" size={20} />
        </button>
      }
    >
      <MenuSection>
        <div className="user-identity__name">{user?.displayName ?? t('account.signedIn')}</div>
        <div className="user-identity__mail">{user?.email}</div>
      </MenuSection>
      <MenuSeparator />
      {/* Above the preferences on purpose. This is the only row that GOES
          somewhere, and it is how you change which business you are looking at
          now that the header's name is a label rather than a switcher — so it
          sits where the eye lands first, not under two settings rows. */}
      <MenuItem icon={<Icon name="building" size={18} />} onClick={() => navigate('/businesses')}>
        {t('account.myBusinesses')}
      </MenuItem>
      <MenuSeparator />
      {/* Keep the menu open while changing the theme — you want to see it
          change. One button, three modes: each click moves light → dark →
          auto (follow the device), and auto is what a new owner starts on. */}
      <div className="menu-row" onClick={(e) => e.stopPropagation()}>
        <span className="menu-row__label">
          <Icon name="contrast" size={18} />
          {t('account.theme')}
        </span>
        <ThemeModeButton
          labels={{ light: t('account.themeLight'), dark: t('account.themeDark'), auto: t('account.themeAuto') }}
          getAriaLabel={(mode) => t('account.themeButton', { mode })}
        />
      </div>

      {/* Portal language — NOT the language the assistant speaks to callers,
          which is per business under Settings › Assistant (doc 20 D1).
          Sits in the same row shape as the theme toggle: both are settings you
          change in place, not commands, and neither closes the menu. */}
      <div className="menu-row" onClick={(e) => e.stopPropagation()}>
        <span className="menu-row__label">
          <Icon name="globe" size={18} />
          {t('account.language')}
        </span>
        {/* The same picker as the signed-out screens; no globe of its own,
            because the row's label already carries one. */}
        <LanguageSelect icon={false} />
      </div>

      <MenuSeparator />
      <MenuItem tone="danger" icon={<Icon name="log-out" size={18} />} onClick={() => void signOutUser()}>
        {t('account.signOut')}
      </MenuItem>
    </Menu>
  );
}
